import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDays } from "date-fns";
import { Search, MapPin } from "lucide-react";
import api from "@/lib/api";
import { DateField } from "@/components/DatePickers";

const TIMES = ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM"];
const shortLoc = (loc) => loc.split(",").slice(1, 3).join(",").trim() || loc;

// Compact date + time + pickup-location search used in city/landing-page heroes.
// Sends the visitor straight to /fleet with their full window so available cars
// are pre-filtered and the pickup location carries through to checkout.
export default function AvailabilitySearch({ className = "" }) {
  const navigate = useNavigate();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [startTime, setStartTime] = useState("10:00 AM");
  const [endTime, setEndTime] = useState("10:00 AM");
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("");

  useEffect(() => {
    api.get("/config")
      .then((r) => {
        const locs = r.data?.pickup_locations || [];
        setLocations(locs);
        if (locs.length) setLoc(locs[0]);
      })
      .catch(() => {});
  }, []);

  const canSearch = start && end && end > start;

  const search = () => {
    if (!canSearch) return;
    const params = new URLSearchParams({ start, end, st: startTime, et: endTime });
    if (loc === "__near__") params.set("sort", "near");
    else if (loc) params.set("loc", loc);
    navigate(`/fleet?${params.toString()}`);
  };

  return (
    <div data-testid="availability-search" className={`bg-white rounded-2xl shadow-card border border-slate-100 p-4 sm:p-5 text-left ${className}`}>
      {locations.length > 0 && (
        <div className="mb-3">
          <span className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1.5"><MapPin size={13} className="text-blue-500" /> Pickup location</span>
          <select
            value={loc}
            onChange={(e) => setLoc(e.target.value)}
            data-testid="hero-pickup-location"
            className="belo-input w-full cursor-pointer"
          >
            <option value="__near__">📍 Near me</option>
            {locations.map((l) => <option key={l} value={l}>{shortLoc(l)}</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="text-xs font-semibold text-slate-500 block mb-1.5">Pickup date</span>
          <DateField value={start} onChange={setStart} fromDate={new Date()} placeholder="Add date" testid="hero-start-date" />
        </div>
        <div>
          <span className="text-xs font-semibold text-slate-500 block mb-1.5">Pickup time</span>
          <select value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="hero-start-time" className="belo-input w-full cursor-pointer">
            {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
          </select>
        </div>
        <div>
          <span className="text-xs font-semibold text-slate-500 block mb-1.5">Return date</span>
          <DateField value={end} onChange={setEnd} fromDate={start ? addDays(new Date(start), 1) : addDays(new Date(), 1)} placeholder="Add date" testid="hero-end-date" />
        </div>
        <div>
          <span className="text-xs font-semibold text-slate-500 block mb-1.5">Return time</span>
          <select value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="hero-end-time" className="belo-input w-full cursor-pointer">
            {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
          </select>
        </div>
      </div>
      <button
        onClick={search}
        disabled={!canSearch}
        data-testid="hero-check-availability"
        className="w-full mt-3 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-40"
      >
        <Search size={16} /> Check availability
      </button>
    </div>
  );
}
